import React from 'react';
import './TermsAndConditions.css';

/**
 * Terms and Conditions Component
 * 
 * Displays the terms and conditions for StoicCredit application.
 * Comprehensive legal terms covering user responsibilities, service usage, subscriptions, and legal protections.
 */
const TermsAndConditions = () => {
  return (
    <div className="terms-container">
      <div className="terms-content">
        <h1>Terms and Conditions</h1>
        <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        
        <section className="terms-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing, using, or registering for StoicCredit ("the Service," "we," "our," "us," or "Company"), you ("User," "you," or "your") accept and agree to be bound by these Terms and Conditions ("Terms") and our Privacy Policy, which is incorporated herein by reference. If you do not agree to these Terms in their entirety, you must not access or use the Service.
          </p>
          <p>
            These Terms constitute a legally binding agreement between you and StoicCredit. Your continued use of the Service after any modifications to these Terms constitutes your acceptance of such modifications.
          </p>
        </section>

        <section className="terms-section">
          <h2>2. Description of Service</h2>
          <p>
            StoicCredit is a digital educational platform that provides informational content, tools, and resources related to business credit building, credit management strategies, and financial planning. Our services include:
          </p>
          <ul>
            <li>Interactive credit planning guides and educational content</li>
            <li>AI-powered credit assessment and recommendation tools</li>
            <li>Personalized credit plan generation</li>
            <li>Subscription-based access to premium content and features</li>
            <li>Chat functionality with AI assistants for credit-related questions</li>
          </ul>
          <p>
            <strong>IMPORTANT:</strong> The Service is provided for educational and informational purposes only. The Service is NOT a credit repair organization and does NOT provide credit repair services as defined under the Credit Repair Organizations Act (CROA). We do not offer services to improve, repair, or modify credit reports or credit scores. The platform provides education and information only.
          </p>
          <p>
            We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice, at our sole discretion.
          </p>
        </section>

        <section className="terms-section">
          <h2>3. Age Restrictions and COPPA Compliance</h2>
          <p>
            The Service is intended solely for users who are 18 years of age or older. By using the Service, you represent and warrant that:
          </p>
          <ul>
            <li>You are at least 18 years old</li>
            <li>You have the legal capacity to enter into binding agreements</li>
            <li>You are not prohibited from using the Service under applicable laws</li>
          </ul>
          <p>
            <strong>Prohibition on Underage Use:</strong> Users under the age of 18 are strictly prohibited from using the Service. We do not knowingly collect, use, or disclose personal information from individuals under 18 years of age. If we become aware that we have collected personal information from a minor, we will take immediate steps to delete such information and terminate the associated account.
          </p>
          <p>
            If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately so we may delete such information.
          </p>
        </section>

        <section className="terms-section">
          <h2>4. User Accounts and Registration</h2>
          
          <h3>4.1 Account Creation</h3>
          <p>
            To access certain features of the Service, you must create an account by providing accurate, current, and complete information. You are solely responsible for:
          </p>
          <ul>
            <li>Maintaining the confidentiality and security of your account credentials</li>
            <li>All activities that occur under your account, whether authorized or not</li>
            <li>Immediately notifying us of any unauthorized access or use of your account</li>
            <li>Ensuring that you exit from your account at the end of each session</li>
          </ul>

          <h3>4.2 Account Security</h3>
          <p>
            You may not share your account credentials with any third party. You are responsible for maintaining the security of your password and for all activities that occur under your account. We are not liable for any loss or damage arising from your failure to comply with this security obligation.
          </p>

          <h3>4.3 Account Termination by User</h3>
          <p>
            You may terminate your account at any time by cancelling your subscription (if applicable) and requesting account deletion through your account settings or by contacting us. Upon termination, your right to use the Service will immediately cease.
          </p>
        </section>

        <section className="terms-section">
          <h2>5. Subscription Terms</h2>
          
          <h3>5.1 Subscription Plans</h3>
          <p>
            We offer various subscription plans with different features and access levels. Subscription fees, billing cycles, and features are subject to change at our sole discretion. Current subscription plans and pricing are displayed on our platform and may be modified at any time.
          </p>

          <h3>5.2 Billing and Payment</h3>
          <ul>
            <li>Subscriptions are billed in advance on a recurring basis (monthly or annually, as selected by you)</li>
            <li>All fees are processed through Stripe, our third-party payment processor</li>
            <li>You authorize us to charge your designated payment method for all subscription fees and any applicable taxes</li>
            <li>You are responsible for providing accurate payment information and maintaining valid payment methods</li>
            <li>If payment fails or is declined, we may suspend or terminate your subscription immediately</li>
            <li>All fees are stated in U.S. dollars unless otherwise specified</li>
          </ul>

          <h3>5.3 Subscription Renewal</h3>
          <p>
            <strong>Automatic Renewal:</strong> Subscriptions automatically renew at the end of each billing period unless cancelled before the renewal date. By subscribing, you acknowledge and agree that:
          </p>
          <ul>
            <li>Your subscription will automatically renew unless you cancel it</li>
            <li>You authorize us to charge your payment method for each renewal period</li>
            <li>You will be charged the then-current subscription price at the time of renewal</li>
            <li>You may cancel your subscription at any time through your account settings or the subscription management portal</li>
            <li>Cancellation must be completed before the renewal date to avoid charges for the next billing period</li>
          </ul>
          <p>
            <strong>State-Specific Disclosures:</strong> If you are a resident of California, New York, Illinois, or Virginia, you have additional rights regarding automatic renewal. Please review your state's consumer protection laws for more information.
          </p>

          <h3>5.4 Price Changes</h3>
          <p>
            We reserve the right to modify subscription prices at any time. We will provide at least 30 days' notice of any price increases. Price changes will apply to subsequent billing cycles. Continued use of the Service after a price change constitutes acceptance of the new pricing. If you do not agree to a price increase, you must cancel your subscription before the price change takes effect.
          </p>
        </section>

        <section className="terms-section">
          <h2>6. Refund Policy</h2>
          
          <h3>6.1 General Refund Policy</h3>
          <p>
            <strong>Subscriptions are generally non-refundable.</strong> All subscription fees are final and non-refundable except as explicitly stated in this Refund Policy or as required by applicable law.
          </p>

          <h3>6.2 Refund Eligibility</h3>
          <p>
            Refunds may be granted in the following limited circumstances:
          </p>
          <ul>
            <li><strong>Duplicate Charges:</strong> If you are charged twice for the same subscription period, we will refund the duplicate charge upon verification</li>
            <li><strong>Accidental Double Subscriptions:</strong> If you accidentally subscribe multiple times, we will refund the duplicate subscriptions</li>
            <li><strong>24-Hour Cooling-Off Period:</strong> New subscribers may request a full refund within 24 hours of the initial subscription purchase, provided you have not substantially used the premium features. "Substantially used" means you have: (a) logged into your account more than 3 times, (b) generated more than 5 AI credit plans or recommendations, (c) accessed more than 10 premium content pages, or (d) used premium features for more than 2 hours of cumulative usage time</li>
            <li><strong>Service Unavailability:</strong> If the Service is unavailable for more than 48 consecutive hours due to our error, you may be eligible for a prorated refund for the affected period</li>
            <li><strong>Termination Without Cause:</strong> If we terminate your account without cause (as defined in Section 17.3), you will receive a prorated refund for the unused portion of your subscription</li>
          </ul>

          <h3>6.3 No Refund for Termination with Cause</h3>
          <p>
            If your account is terminated for cause (including but not limited to violation of these Terms, fraud, abuse, or illegal activity), you will NOT be entitled to any refund, and all fees paid are forfeited.
          </p>

          <h3>6.4 Refund Process</h3>
          <p>
            To request a refund, contact us through <a href="mailto:support@stoiccredit.com">support@stoiccredit.com</a>. Refund requests must be submitted within 30 days of the charge. We reserve the right to deny refund requests that do not meet the criteria outlined in this section.
          </p>

          <h3>6.5 Payment Disputes</h3>
          <p>
            If you initiate a chargeback or dispute with your payment provider (including Stripe disputes), we may:
          </p>
          <ul>
            <li>Suspend or terminate your account pending resolution of the dispute</li>
            <li>Challenge the dispute with evidence of your use of the Service and subscription terms</li>
            <li>Reserve the right to pursue collection of any disputed amounts plus applicable fees</li>
            <li>Prohibit you from using the Service in the future if the dispute is determined to be fraudulent or in bad faith</li>
          </ul>
          <p>
            We encourage you to contact us directly to resolve billing issues before initiating a chargeback. Unauthorized chargebacks or disputes that are not based on legitimate billing errors may result in account termination. We reserve the right to pursue legal remedies to recover costs and fees associated with fraudulent or bad-faith disputes.
          </p>
        </section>

        <section className="terms-section">
          <h2>7. Use of Service</h2>
          
          <h3>7.1 Permitted Use</h3>
          <p>
            You may use the Service solely for lawful purposes and in accordance with these Terms. You agree to use the Service only for your personal or business credit planning and educational purposes.
          </p>

          <h3>7.2 Prohibited Uses</h3>
          <p>
            You agree NOT to:
          </p>
          <ul>
            <li>Use the Service in any way that violates any applicable federal, state, local, or international law or regulation</li>
            <li>Transmit any material that is abusive, harassing, defamatory, obscene, threatening, or illegal</li>
            <li>Attempt to gain unauthorized access to any portion of the Service, other accounts, computer systems, or networks connected to the Service</li>
            <li>Use automated systems (bots, scrapers, crawlers, etc.) to access, monitor, or copy any content from the Service without our express written permission</li>
            <li>Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Service</li>
            <li>Interfere with or disrupt the Service, servers, or networks connected to the Service</li>
            <li>Reproduce, duplicate, copy, sell, resell, or commercially exploit any portion of the Service, use of the Service, or access to the Service without our express written permission</li>
            <li>Use the Service to provide commercial services to third parties, create commercial derivative works, or build competing products or services</li>
            <li>Share your account credentials or allow others to access your account</li>
            <li>Remove, alter, or obscure any copyright, trademark, or other proprietary notices from the Service</li>
            <li><strong>Commercially share, resell, redistribute, or publicly display AI-generated outputs or recommendations from the Service for commercial purposes</strong></li>
            <li><strong>Upload credit reports, financial documents, or personal information belonging to others without their explicit written consent</strong></li>
            <li><strong>Scrape, extract, or collect content from the Service for training machine learning models, creating datasets, or any commercial purpose</strong></li>
            <li><strong>Use the Service's content or AI outputs to commercially train, fine-tune, or improve other AI models or services</strong></li>
            <li>Impersonate any person or entity or falsely state or misrepresent your affiliation with any person or entity</li>
            <li>Upload or transmit any viruses, malware, or other harmful code</li>
          </ul>
          <p>
            Violation of any prohibited use may result in immediate account termination without refund.
          </p>
        </section>

        <section className="terms-section">
          <h2>8. Intellectual Property Rights</h2>
          
          <h3>8.1 Our Content</h3>
          <p>
            The Service and its original content, features, functionality, design, text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software are owned by StoicCredit and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property laws. All rights not expressly granted are reserved.
          </p>

          <h3>8.2 Limited License to Use Service</h3>
          <p>
            Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Service for your personal, non-commercial use only. This license does not include any right to:
          </p>
          <ul>
            <li>Resell or make commercial use of the Service or its contents</li>
            <li>Collect or use any product listings, descriptions, or prices</li>
            <li>Make any derivative use of the Service or its contents</li>
            <li>Use any data mining, robots, or similar data gathering or extraction tools</li>
          </ul>

          <h3>8.3 User Content License</h3>
          <p>
            You retain ownership of any content you submit, post, upload, or display on or through the Service ("User Content"). By submitting User Content, you grant us a <strong>limited, non-exclusive, royalty-free, worldwide license</strong> to use, reproduce, modify, adapt, and process your User Content <strong>solely for the purpose of providing and improving the Service</strong>.
          </p>
          <p>
            <strong>Important Limitations:</strong>
          </p>
          <ul>
            <li>We will <strong>NOT</strong> use your User Content for marketing, advertising, or public distribution without your explicit consent</li>
            <li>Sensitive financial documents, credit reports, and personal financial information are explicitly protected and will only be used to provide the Service</li>
            <li>We will not sell, rent, or license your User Content to third parties</li>
            <li>You may revoke this license by deleting your User Content and account, subject to our data retention policies</li>
          </ul>

          <h3>8.4 Feedback</h3>
          <p>
            Any feedback, comments, suggestions, ideas, or other information you provide regarding the Service ("Feedback") may be used by us without any obligation to compensate you. By providing Feedback, you grant us a perpetual, irrevocable, royalty-free, worldwide license to use, modify, and incorporate such Feedback into the Service.
          </p>
        </section>

        <section className="terms-section">
          <h2>9. AI-Generated Content and Recommendations</h2>
          <p>
            Our Service uses artificial intelligence (AI) and machine learning technologies to generate credit plans, recommendations, responses, and other content. <strong>You acknowledge and agree that:</strong>
          </p>
          <ul>
            <li><strong>AI-generated content is provided for informational and educational purposes only</strong></li>
            <li><strong>AI recommendations are NOT financial, legal, tax, or professional advice</strong></li>
            <li><strong>AI output may contain errors, inaccuracies, or outdated information</strong></li>
            <li><strong>AI technology is experimental and may produce unexpected or incorrect results</strong></li>
            <li>You should consult with qualified financial, legal, and tax professionals before making any financial decisions</li>
            <li>We do not guarantee the accuracy, completeness, suitability, or reliability of AI-generated content</li>
            <li>You use AI-generated content at your own risk</li>
            <li>We are not responsible for any decisions, actions, or outcomes based on AI-generated content</li>
            <li>AI responses are generated based on patterns in training data and may not reflect current laws, regulations, or best practices</li>
            <li>You should independently verify any information provided by AI before relying on it</li>
          </ul>
          <p>
            <strong>NO RELIANCE:</strong> You agree not to rely solely on AI-generated content for making financial, legal, or business decisions. Always seek advice from qualified professionals.
          </p>
        </section>

        <section className="terms-section">
          <h2>10. Educational Purposes Only - No Credit Repair Services</h2>
          <p>
            <strong>IMPORTANT DISCLAIMER:</strong> StoicCredit is NOT a credit repair organization as defined under the Credit Repair Organizations Act (CROA), 15 U.S.C. § 1679 et seq.
          </p>
          <p>
            The Service does NOT:
          </p>
          <ul>
            <li>Provide credit repair services</li>
            <li>Improve, repair, or modify credit reports or credit scores</li>
            <li>Remove negative information from credit reports</li>
            <li>Guarantee any specific credit outcomes</li>
            <li>Act as an intermediary between you and credit bureaus</li>
            <li>Provide services to "clean up" or "fix" credit</li>
          </ul>
          <p>
            The Service provides educational information, tools, and resources to help you understand credit systems and make informed decisions. All content, recommendations, and AI-generated advice are for educational purposes only and do not constitute professional financial, legal, or credit repair services.
          </p>
        </section>

        <section className="terms-section">
          <h2>11. Disclaimers</h2>
          
          <h3>11.1 Educational Purpose</h3>
          <p>
            The Service is provided for educational and informational purposes only. The content, tools, recommendations, and AI-generated advice provided are NOT intended to constitute financial, legal, tax, accounting, or professional advice of any kind. You should consult with qualified professionals (including financial advisors, attorneys, accountants, and credit counselors) before making any financial, legal, or business decisions.
          </p>

          <h3>11.2 No Warranties</h3>
          <p>
            <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.</strong>
          </p>
          <p>
            We do not warrant that:
          </p>
          <ul>
            <li>The Service will be uninterrupted, secure, or error-free</li>
            <li>Any defects or errors will be corrected</li>
            <li>The Service is free of viruses, malware, or other harmful components</li>
            <li>The results obtained from using the Service will be accurate, reliable, or meet your expectations</li>
            <li>The Service will be available at any particular time or location</li>
          </ul>

          <h3>11.3 Credit Building Results</h3>
          <p>
            We do not guarantee any specific results from using our Service. Credit building outcomes depend on numerous factors beyond our control, including but not limited to:
          </p>
          <ul>
            <li>Your financial situation and credit history</li>
            <li>Lender policies and underwriting criteria</li>
            <li>Economic conditions and market factors</li>
            <li>Your ability to implement recommendations</li>
            <li>Changes in credit reporting practices or regulations</li>
          </ul>
          <p>
            <strong>NO GUARANTEES:</strong> We make no representations or warranties regarding credit score improvements, credit approvals, loan terms, or any other financial outcomes.
          </p>
        </section>

        <section className="terms-section">
          <h2>12. Limitation of Liability</h2>
          <p>
            <strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL STOICCREDIT, ITS AFFILIATES, AGENTS, DIRECTORS, EMPLOYEES, SUPPLIERS, LICENSORS, OR SERVICE PROVIDERS BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO THE USE OF, OR INABILITY TO USE, THE SERVICE.</strong>
          </p>
          <p>
            <strong>SPECIFIC EXCLUSIONS:</strong> We are NOT liable for:
          </p>
          <ul>
            <li>Lost credit opportunities or credit denials</li>
            <li>Decreased credit scores or negative credit impacts</li>
            <li>Financial losses, lost profits, or lost business opportunities</li>
            <li>Costs of substitute services</li>
            <li>Any decisions made based on Service content or AI recommendations</li>
            <li>Third-party actions or service failures</li>
            <li>Data loss or corruption</li>
            <li>Unauthorized access to your account due to your failure to maintain security</li>
          </ul>
          <p>
            <strong>MAXIMUM LIABILITY:</strong> Our total liability for any claims arising from or related to the Service, regardless of the form of action (whether in contract, tort, strict liability, or otherwise), shall not exceed the amount you paid us in the twelve (12) months prior to the event giving rise to the liability, or one hundred dollars ($100.00), whichever is greater.
          </p>
          <p>
            Some jurisdictions do not allow the exclusion of certain warranties or the limitation or exclusion of liability for incidental or consequential damages. Accordingly, some of the above limitations may not apply to you, but in such cases, our liability will be limited to the fullest extent permitted by applicable law.
          </p>
        </section>

        <section className="terms-section">
          <h2>13. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless StoicCredit and its affiliates, officers, directors, employees, agents, licensors, and service providers from and against any claims, liabilities, damages, losses, and expenses, including without limitation reasonable attorney's fees and costs, arising out of or in any way connected with:
          </p>
          <ul>
            <li>Your access to or use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party right, including without limitation any intellectual property right, privacy right, or publicity right</li>
            <li>Any User Content you submit, post, upload, or transmit through the Service</li>
            <li>Your violation of any applicable law or regulation</li>
            <li>Any decisions or actions you take based on Service content or AI recommendations</li>
          </ul>
          <p>
            We reserve the right to assume the exclusive defense and control of any matter subject to indemnification by you, in which event you will cooperate with us in asserting any available defenses.
          </p>
        </section>

        <section className="terms-section">
          <h2>14. Third-Party Services</h2>
          <p>
            The Service may integrate with or use third-party services, including but not limited to:
          </p>
          <ul>
            <li><strong>Firebase (Google):</strong> For authentication, data storage, and analytics</li>
            <li><strong>Stripe:</strong> For payment processing and subscription management</li>
            <li><strong>AI Service Providers (OpenAI/Gemini):</strong> For generating credit plans and recommendations</li>
          </ul>
          <p>
            <strong>Third-Party Disclaimer:</strong> Your use of third-party services is subject to their respective terms and conditions and privacy policies. We are NOT responsible for:
          </p>
          <ul>
            <li>Third-party service outages, downtime, or unavailability</li>
            <li>Data loss, corruption, or breaches occurring on third-party systems</li>
            <li>Errors, bugs, or malfunctions in third-party services</li>
            <li>Third-party service changes that affect the Service</li>
            <li>Any actions or inactions of third-party service providers</li>
          </ul>
          <p>
            You must comply with all applicable third-party terms. We disclaim all liability related to third-party services to the fullest extent permitted by law. If a third-party service becomes unavailable or changes in a way that affects the Service, we are not obligated to provide alternative solutions or refunds.
          </p>
        </section>

        <section className="terms-section">
          <h2>15. Data Retention and Deletion Policy</h2>
          
          <h3>15.1 Data Retention After Account Deletion</h3>
          <p>
            When you request account deletion, we will:
          </p>
          <ul>
            <li>Delete your account and personal information within 30 days of your request</li>
            <li>Retain certain data for compliance, legal, and business purposes as described below</li>
            <li>Permanently delete user-generated content (assessments, chat history) within 90 days, unless required for legal compliance</li>
          </ul>

          <h3>15.2 Data Retained for Compliance</h3>
          <p>
            We may retain certain information after account deletion for:
          </p>
          <ul>
            <li>Legal compliance and regulatory requirements</li>
            <li>Dispute resolution and enforcement of these Terms</li>
            <li>Fraud prevention and security</li>
            <li>Financial record-keeping (billing and payment records) for up to 7 years as required by law</li>
            <li>Analytics and aggregated data (anonymized, without personal identifiers)</li>
          </ul>

          <h3>15.3 Permanent Deletion</h3>
          <p>
            Data that is not required for compliance will be permanently deleted from our active systems within 90 days of account deletion. Backups may retain data for up to 1 year, after which it will be permanently deleted.
          </p>

          <h3>15.4 AI Training Data and Jurisdictional Compliance</h3>
          <p>
            User Content and interactions with AI features may be used to improve our AI models and services. If you do not want your data used for AI training, you may opt out by contacting us.
          </p>
          <p>
            <strong>Data in Trained Models:</strong> Data that has been incorporated into trained AI models may be retained in aggregated, anonymized form and cannot be individually removed from such models. However, we will comply with applicable data protection laws, including:
          </p>
          <ul>
            <li><strong>GDPR (European Users):</strong> We will comply with GDPR requirements, including the right to erasure, to the extent technically feasible. Aggregated and anonymized data used in trained models may be retained if it no longer constitutes personal data under GDPR</li>
            <li><strong>CCPA (California Users):</strong> We will comply with CCPA deletion requests. Aggregated and anonymized data that cannot be linked to you may be retained</li>
            <li><strong>Other Jurisdictions:</strong> We will comply with applicable data protection laws in your jurisdiction to the extent required by law</li>
          </ul>
          <p>
            If you are subject to GDPR, CCPA, or other data protection laws, please contact us to exercise your rights, and we will process your request in accordance with applicable law.
          </p>
        </section>

        <section className="terms-section">
          <h2>16. DMCA Notice and Copyright Procedure</h2>
          
          <h3>16.1 DMCA Agent</h3>
          <p>
            StoicCredit respects intellectual property rights and expects users to do the same. If you believe that content on the Service infringes your copyright, please contact our designated DMCA Agent:
          </p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:support@stoiccredit.com">support@stoiccredit.com</a></li>
          </ul>

          <h3>16.2 Takedown Request Process</h3>
          <p>
            To file a DMCA takedown notice, you must provide:
          </p>
          <ul>
            <li>A physical or electronic signature of the copyright owner or authorized agent</li>
            <li>Identification of the copyrighted work claimed to be infringed</li>
            <li>Identification of the infringing material and its location on the Service</li>
            <li>Your contact information (name, address, phone, email)</li>
            <li>A statement that you have a good faith belief that the use is not authorized</li>
            <li>A statement that the information is accurate and, under penalty of perjury, that you are authorized to act on behalf of the copyright owner</li>
          </ul>

          <h3>16.3 Counter-Notification Process</h3>
          <p>
            If you believe your content was removed in error, you may file a counter-notification with our DMCA Agent containing:
          </p>
          <ul>
            <li>Your physical or electronic signature</li>
            <li>Identification of the removed content and its location before removal</li>
            <li>A statement under penalty of perjury that you have a good faith belief the content was removed by mistake</li>
            <li>Your name, address, and consent to jurisdiction</li>
          </ul>

          <h3>16.4 Safe Harbor Protections</h3>
          <p>
            We comply with the Digital Millennium Copyright Act (DMCA) safe harbor provisions. We will respond to valid DMCA notices and may remove or disable access to allegedly infringing content. Repeat infringers may have their accounts terminated.
          </p>
        </section>

        <section className="terms-section">
          <h2>17. Termination</h2>
          
          <h3>17.1 Termination by You</h3>
          <p>
            You may terminate your account and stop using the Service at any time by cancelling your subscription (if applicable) and requesting account deletion through your account settings or by contacting us.
          </p>

          <h3>17.2 Termination by Us - With Cause</h3>
          <p>
            We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for cause, including but not limited to:
          </p>
          <ul>
            <li>Breach of these Terms</li>
            <li>Fraudulent, abusive, or illegal activity</li>
            <li>Non-payment of subscription fees</li>
            <li>Violation of prohibited uses</li>
            <li>Sharing account credentials</li>
            <li>Attempting to harm or disrupt the Service</li>
            <li>Violation of intellectual property rights</li>
          </ul>
          <p>
            <strong>No Refund for Termination with Cause:</strong> If your account is terminated for cause, you will NOT be entitled to any refund, and all fees paid are forfeited.
          </p>

          <h3>17.3 Termination by Us - Without Cause</h3>
          <p>
            We reserve the right to terminate your account at any time, with or without cause, at our sole discretion. If we terminate your account without cause, you will receive a prorated refund for the unused portion of your subscription, calculated from the date of termination to the end of your current billing period.
          </p>

          <h3>17.4 Effect of Termination</h3>
          <p>
            Upon termination:
          </p>
          <ul>
            <li>Your right to use the Service will immediately cease</li>
            <li>We may delete your account and User Content in accordance with our data retention policy</li>
            <li>All provisions of these Terms that by their nature should survive termination shall survive, including but not limited to: intellectual property rights, warranty disclaimers, indemnity, limitation of liability, and arbitration provisions</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>18. Enforcement</h2>
          <p>
            We enforce these Terms in our sole discretion and in good faith. You acknowledge and agree that:
          </p>
          <ul>
            <li>We may investigate suspicious activity, violations, or potential abuse of the Service</li>
            <li>Our enforcement decisions are made in our sole discretion</li>
            <li>We are not obligated to provide warnings before taking enforcement action</li>
            <li>Different violations may result in different enforcement actions based on the severity and nature of the violation</li>
            <li>We may take action based on patterns of behavior, not just individual incidents</li>
            <li>Enforcement actions may include warnings, temporary suspensions, or permanent termination</li>
          </ul>
          <p>
            If you believe enforcement action was taken in error, you may contact us to appeal. We will review your appeal in good faith, but our decision regarding enforcement actions is final.
          </p>
        </section>

        <section className="terms-section">
          <h2>19. Data and Privacy</h2>
          <p>
            Your use of the Service is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your information. By using the Service, you consent to the collection and use of your information as described in the Privacy Policy.
          </p>
        </section>

        <section className="terms-section">
          <h2>20. Force Majeure</h2>
          <p>
            We shall not be liable for any failure or delay in performance under these Terms that is due to causes beyond our reasonable control, including but not limited to:
          </p>
          <ul>
            <li>Acts of God, natural disasters, or severe weather</li>
            <li>War, terrorism, riots, or civil unrest</li>
            <li>Government actions, laws, regulations, or orders</li>
            <li>Pandemics, epidemics, or public health emergencies</li>
            <li>Strikes, labor disputes, or work stoppages</li>
            <li>Internet or telecommunications failures or outages</li>
            <li>Third-party service provider failures (including but not limited to Firebase, Stripe, or AI service providers)</li>
            <li>Cyberattacks, data breaches, or security incidents</li>
            <li>Power outages or infrastructure failures</li>
          </ul>
          <p>
            If a force majeure event continues for more than 30 days, either party may terminate these Terms upon written notice. We will use reasonable efforts to minimize the impact of force majeure events and resume normal operations as soon as practicable.
          </p>
        </section>

        <section className="terms-section">
          <h2>21. Export Control and International Use</h2>
          <p>
            The Service may be subject to United States export control laws and regulations. You agree to comply with all applicable export and re-export control laws and regulations, including:
          </p>
          <ul>
            <li>The Export Administration Regulations maintained by the U.S. Department of Commerce</li>
            <li>Trade and economic sanctions maintained by the U.S. Treasury Department's Office of Foreign Assets Control</li>
            <li>Any other applicable export control laws in your jurisdiction</li>
          </ul>
          <p>
            You represent and warrant that: (a) you are not located in a country that is subject to a U.S. government embargo or that has been designated by the U.S. government as a "terrorist supporting" country; (b) you are not listed on any U.S. government list of prohibited or restricted parties; and (c) you will not use the Service for any purpose prohibited by export control laws.
          </p>
          <p>
            If you access the Service from outside the United States, you are responsible for compliance with local laws in your jurisdiction.
          </p>
        </section>

        <section className="terms-section">
          <h2>22. Electronic Signatures and Communications</h2>
          <p>
            By accessing or using the Service, you consent to receive communications from us electronically, including by email, through the Service, or through other electronic means. You agree that all agreements, notices, disclosures, and other communications that we provide to you electronically satisfy any legal requirement that such communications be in writing.
          </p>
          <p>
            <strong>Electronic Acceptance:</strong> Your use of the Service, including clicking "I agree" or similar acceptance mechanisms, clicking through these Terms, or creating an account, constitutes your electronic signature and acceptance of these Terms. You acknowledge that electronic signatures have the same legal effect as handwritten signatures under applicable law, including the Electronic Signatures in Global and National Commerce Act (E-SIGN Act) and the Uniform Electronic Transactions Act (UETA).
          </p>
        </section>

        <section className="terms-section">
          <h2>23. Assignment</h2>
          <p>
            You may not assign, transfer, or delegate these Terms or any of your rights or obligations hereunder without our prior written consent. Any attempted assignment, transfer, or delegation in violation of this section shall be null and void.
          </p>
          <p>
            We may assign, transfer, or delegate these Terms or any of our rights or obligations hereunder without your consent, including in connection with:
          </p>
          <ul>
            <li>A merger, acquisition, or sale of all or substantially all of our assets</li>
            <li>A corporate reorganization or restructuring</li>
            <li>Any other business transaction or transfer of ownership</li>
          </ul>
          <p>
            Upon any assignment, these Terms will bind and inure to the benefit of the parties and their respective successors and permitted assigns.
          </p>
        </section>

        <section className="terms-section">
          <h2>24. Changes to Terms</h2>
          <p>
            We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
          </p>
          <p>
            Material changes may include, but are not limited to:
          </p>
          <ul>
            <li>Changes to subscription pricing or billing terms</li>
            <li>Significant changes to service features or availability</li>
            <li>Changes to dispute resolution procedures</li>
            <li>Changes to refund policies</li>
          </ul>
          <p>
            We will notify you of material changes by:
          </p>
          <ul>
            <li>Posting a notice on the Service</li>
            <li>Sending an email to the address associated with your account</li>
            <li>Updating the "Last Updated" date at the top of these Terms</li>
          </ul>
          <p>
            By continuing to access or use the Service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you must stop using the Service and may cancel your subscription in accordance with Section 5.
          </p>
        </section>

        <section className="terms-section">
          <h2>25. Governing Law and Dispute Resolution</h2>
          
          <h3>25.1 Governing Law</h3>
          <p>
            <strong>These Terms shall be governed by and construed in accordance with the laws of the State of Florida, United States of America, without regard to its conflict of law provisions.</strong> Any legal action or proceeding arising under these Terms will be brought exclusively in the federal or state courts located in Florida, and you hereby consent to the personal jurisdiction and venue of such courts.
          </p>
          <p>
            <strong>Jurisdictional Savings Clause:</strong> To the extent required by applicable law in your jurisdiction, the consumer protection laws of your state or country shall apply. This includes, but is not limited to:
          </p>
          <ul>
            <li>California users retain CCPA protections regardless of Florida choice of law</li>
            <li>European Union users retain GDPR protections regardless of Florida choice of law</li>
            <li>Other mandatory consumer protection laws in your jurisdiction that cannot be waived by choice of law provisions</li>
          </ul>
          <p>
            This savings clause does not affect the venue or jurisdiction provisions above, which remain in effect.
          </p>

          <h3>25.2 Arbitration Agreement</h3>
          <p>
            <strong>MANDATORY ARBITRATION:</strong> Except as provided below, any dispute, controversy, or claim arising out of or relating to these Terms, the Service, or the breach, termination, enforcement, interpretation, or validity thereof (a "Dispute") shall be resolved exclusively through binding arbitration in accordance with the Commercial Arbitration Rules of the American Arbitration Association ("AAA") then in effect, except as modified by this Section.
          </p>

          <h3>25.3 Arbitration Procedures</h3>
          <ul>
            <li><strong>Venue:</strong> Arbitration will be conducted in Florida, USA, or at another location mutually agreed upon by the parties</li>
            <li><strong>Rules:</strong> The arbitration will be conducted in accordance with the AAA Commercial Arbitration Rules</li>
            <li><strong>Arbitrator:</strong> A single arbitrator will be selected in accordance with AAA rules</li>
            <li><strong>Discovery:</strong> Limited discovery will be permitted as allowed by AAA rules</li>
            <li><strong>Confidentiality:</strong> The arbitration proceedings and award will be confidential</li>
          </ul>

          <h3>25.4 Small Claims Court Carve-Out</h3>
          <p>
            Notwithstanding the arbitration requirement, either party may bring an individual action in small claims court if the claim qualifies for small claims court jurisdiction. This carve-out does not apply to class actions or representative actions.
          </p>

          <h3>25.5 No Class Actions</h3>
          <p>
            <strong>YOU AND WE AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.</strong> The arbitrator may not consolidate more than one person's claims and may not otherwise preside over any form of class or representative proceeding.
          </p>

          <h3>25.6 Opt-Out Procedure</h3>
          <p>
            You have the right to opt out of this arbitration agreement. To opt out, you must send us written notice by certified mail, return receipt requested, within 30 days of first accepting these Terms. Your opt-out notice must include:
          </p>
          <ul>
            <li>Your full legal name and account email address</li>
            <li>Your clear statement that you wish to opt out of the arbitration agreement in Section 25</li>
            <li>Your complete mailing address</li>
            <li>Your signature (electronic signatures are acceptable)</li>
          </ul>
          <p>
            Send your opt-out notice to: <a href="mailto:support@stoiccredit.com">support@stoiccredit.com</a>
          </p>
          <p>
            <strong>Mutual Opt-Out:</strong> If you validly opt out of arbitration, we also reserve the right to opt out of arbitration for any disputes with you, in which case disputes will be resolved in state or federal courts in Florida as provided in Section 25.1.
          </p>
          <p>
            If you do not opt out within the 30-day period, you will be bound by the arbitration agreement.
          </p>

          <h3>25.7 Exceptions to Arbitration</h3>
          <p>
            The following disputes are NOT subject to arbitration:
          </p>
          <ul>
            <li>Disputes seeking injunctive or other equitable relief for alleged intellectual property infringement</li>
            <li>Disputes in small claims court (as provided in Section 25.4)</li>
            <li>Disputes that cannot be arbitrated under applicable law</li>
          </ul>

          <h3>25.8 Waiver of Jury Trial</h3>
          <p>
            YOU AND WE HEREBY WAIVE ANY CONSTITUTIONAL AND STATUTORY RIGHTS TO GO TO COURT AND HAVE A TRIAL IN FRONT OF A JUDGE OR JURY, instead electing to have disputes resolved through arbitration.
          </p>
        </section>

        <section className="terms-section">
          <h2>26. Severability</h2>
          <p>
            If any provision of these Terms is held to be invalid, illegal, or unenforceable by a court of competent jurisdiction, the remaining provisions of these Terms will remain in full force and effect. The invalid, illegal, or unenforceable provision will be replaced with a valid, enforceable provision that most closely matches the intent of the original provision.
          </p>
          <p>
            If a court determines that any provision is invalid or unenforceable, but that by limiting such provision it would become valid and enforceable, then such provision will be deemed to be written, construed, and enforced as so limited.
          </p>
        </section>

        <section className="terms-section">
          <h2>27. Survival Clause</h2>
          <p>
            The following provisions will survive termination of these Terms and your use of the Service:
          </p>
          <ul>
            <li><strong>Intellectual Property Rights:</strong> All provisions relating to ownership, licenses, and intellectual property</li>
            <li><strong>Warranty Disclaimers:</strong> All disclaimers of warranties and limitations on liability</li>
            <li><strong>Indemnity:</strong> Your obligation to indemnify and hold us harmless</li>
            <li><strong>Limitation of Liability:</strong> All limitations on our liability</li>
            <li><strong>Arbitration:</strong> The arbitration agreement and dispute resolution procedures</li>
            <li><strong>Governing Law:</strong> The choice of law and venue provisions</li>
            <li><strong>Payment Obligations:</strong> Any outstanding payment obligations</li>
            <li><strong>Data Retention:</strong> Our rights to retain data as provided in Section 15</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>28. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy and any other legal notices published by us on the Service, constitute the entire agreement between you and StoicCredit regarding the Service and supersede all prior and contemporaneous agreements, proposals, representations, and understandings, whether written or oral, relating to the subject matter of these Terms.
          </p>
          <p>
            No modification, amendment, or waiver of any provision of these Terms will be effective unless in writing and signed by both parties, except as provided in Section 24 (Changes to Terms).
          </p>
        </section>

        <section className="terms-section">
          <h2>29. Waiver</h2>
          <p>
            No waiver of any term or condition of these Terms shall be deemed a further or continuing waiver of such term or condition or any other term or condition. Any failure to assert a right or provision under these Terms shall not constitute a waiver of such right or provision. Our failure to enforce any right or provision will not be considered a waiver of such right or provision.
          </p>
          <p>
            Waivers must be in writing and signed by the party granting the waiver to be effective.
          </p>
        </section>

        <section className="terms-section">
          <h2>30. Contact Information</h2>
          <p>
            If you have any questions about these Terms and Conditions, please contact us:
          </p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:support@stoiccredit.com">support@stoiccredit.com</a></li>
          </ul>
          <p>
            For legal notices, DMCA takedown requests, or arbitration opt-out notices, please use the specific contact methods provided in the relevant sections above.
          </p>
        </section>

        <section className="terms-section">
          <h2>31. Acknowledgment</h2>
          <p>
            <strong>BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS AND CONDITIONS, UNDERSTAND THEM, AND AGREE TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT USE THE SERVICE.</strong>
          </p>
          <p>
            You further acknowledge that:
          </p>
          <ul>
            <li>You have had the opportunity to review these Terms with legal counsel if desired</li>
            <li>You understand the arbitration agreement and your right to opt out</li>
            <li>You understand the limitations on liability and warranty disclaimers</li>
            <li>You agree to resolve disputes through arbitration (unless you opt out)</li>
            <li>You understand that the Service is for educational purposes only and does not provide credit repair services</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default TermsAndConditions;
